import { useEffect } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { agentMailboxSelfService } from "@/services/agent/AgentMailboxSelfService";
import type { MailboxConversationState } from "@/types/agent-mailboxes";

const PAGE_LIMIT = 25;

// Mirrors supabase/functions/webhooks/mail.ts's `agent-mailbox-${agent_id}`
// broadcast, sent when HG Mail reports a conversation.* event. Scoping the
// channel name per agent (rather than a single global topic) means only
// clients viewing this mailbox receive the broadcast.
export const useAgentMailboxRealtime = (agentId: string | undefined) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!agentId) return;

    const channel = supabase
      .channel(`agent-mailbox-${agentId}`)
      .on("broadcast", { event: "conversation.message_received" }, () => {
        queryClient.invalidateQueries({ queryKey: ["agent-mailbox-conversations", agentId] });
      })
      .on("broadcast", { event: "conversation.agent_replied" }, ({ payload }: { payload: { conversation_id: string | null } }) => {
        queryClient.invalidateQueries({ queryKey: ["agent-mailbox-conversations", agentId] });
        if (payload.conversation_id) {
          queryClient.invalidateQueries({ queryKey: ["agent-mailbox-conversation", agentId, payload.conversation_id] });
        }
      })
      .on("broadcast", { event: "conversation.escalated" }, ({ payload }: { payload: { conversation_id: string | null } }) => {
        queryClient.invalidateQueries({ queryKey: ["agent-mailbox-conversations", agentId] });
        if (payload.conversation_id) {
          queryClient.invalidateQueries({ queryKey: ["agent-mailbox-conversation", agentId, payload.conversation_id] });
        }
      })
      .on("broadcast", { event: "conversation.human_replied" }, ({ payload }: { payload: { conversation_id: string | null } }) => {
        queryClient.invalidateQueries({ queryKey: ["agent-mailbox-conversations", agentId] });
        if (payload.conversation_id) {
          queryClient.invalidateQueries({ queryKey: ["agent-mailbox-conversation", agentId, payload.conversation_id] });
        }
      })
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [agentId, queryClient]);
};

export const useAgentMailboxConversations = (
  agentId: string | undefined,
  filters: { state?: MailboxConversationState },
) =>
  useInfiniteQuery({
    queryKey: ["agent-mailbox-conversations", agentId, filters],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      agentMailboxSelfService.listConversations(agentId!, { ...filters, cursor: pageParam, limit: PAGE_LIMIT }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled: !!agentId,
  });

export const useAgentMailboxConversation = (agentId: string | undefined, conversationId: string | undefined) =>
  useQuery({
    queryKey: ["agent-mailbox-conversation", agentId, conversationId],
    queryFn: () => agentMailboxSelfService.getConversation(agentId!, conversationId!),
    enabled: !!agentId && !!conversationId,
  });

export const useSendAgentMailboxReply = (agentId: string | undefined, conversationId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bodyText: string) => agentMailboxSelfService.sendReply(agentId!, conversationId!, bodyText),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-mailbox-conversation", agentId, conversationId] });
      queryClient.invalidateQueries({ queryKey: ["agent-mailbox-conversations", agentId] });
    },
  });
};
